// src/features/activity/ActivityDetailRail.tsx
// One activity: what happened, who did it, what it was about, and — for
// decisions — the recorded rationale. Right rail, not a page.

import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { Badge } from '@react/components';
import type { Activity } from '@core/types/activity';
import { categoryKey, scopeKey, fmtDateTime } from './logic';

interface ActivityDetailRailProps {
  activity: Activity;
  onClose: () => void;
}

function entityLabel(e?: { type_name?: string; id?: string }): string {
  if (!e || !e.id) return '—';
  return e.type_name ? `${e.type_name} · ${e.id}` : e.id;
}

export const ActivityDetailRail = observer(({ activity, onClose }: ActivityDetailRailProps) => {
  const { t, locale } = useI18n();
  const details = activity.details ?? {};
  const detailEntries = Object.entries(details);

  return (
    <aside className="w-full shrink-0 space-y-3 overflow-y-auto border-l border-border p-4 lg:w-[26rem]">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-foreground">{t('activity.detail.title')}</h2>
        <button onClick={onClose} aria-label={t('common.close')} className="rounded-md p-1 text-muted-foreground hover:bg-muted">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
        </button>
      </div>

      <div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={activity.category === 'ACTIVITY_CATEGORY_DECISION' ? 'primary' : 'secondary'}>
            {t(`activity.category.${categoryKey(activity.category)}`)}
          </Badge>
          <span className="text-xs text-muted-foreground">{activity.verb}</span>
        </div>
        <p className="mt-2 text-sm text-foreground">{activity.summary}</p>
      </div>

      <dl className="space-y-2 text-sm">
        <div className="flex justify-between gap-2">
          <dt className="text-muted-foreground">{t('activity.detail.actor')}</dt>
          <dd className="text-right text-foreground">{activity.actor?.display_name || activity.actor?.entity?.id || '—'}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-muted-foreground">{t('activity.detail.object')}</dt>
          <dd className="text-right text-foreground">{entityLabel(activity.object)}</dd>
        </div>
        {activity.target && (
          <div className="flex justify-between gap-2">
            <dt className="text-muted-foreground">{t('activity.detail.target')}</dt>
            <dd className="text-right text-foreground">{entityLabel(activity.target)}</dd>
          </div>
        )}
        <div className="flex justify-between gap-2">
          <dt className="text-muted-foreground">{t('activity.detail.scope')}</dt>
          <dd className="text-right text-foreground">{t(`activity.scope.${scopeKey(activity.scope)}`)}{activity.scope_id ? ` · ${activity.scope_id}` : ''}</dd>
        </div>
        {activity.origin && (
          <div className="flex justify-between gap-2">
            <dt className="text-muted-foreground">{t('activity.detail.origin')}</dt>
            <dd className="text-right text-foreground">{activity.origin}</dd>
          </div>
        )}
        <div className="flex justify-between gap-2">
          <dt className="text-muted-foreground">{t('activity.detail.occurred')}</dt>
          <dd className="text-right text-foreground">{fmtDateTime(activity.created_at as unknown as string, locale)}</dd>
        </div>
      </dl>

      {detailEntries.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('activity.detail.details')}</h3>
          <dl className="mt-1 space-y-1 text-sm">
            {detailEntries.map(([k, v]) => (
              <div key={k} className="flex justify-between gap-2">
                <dt className="text-muted-foreground">{k}</dt>
                <dd className="text-right text-foreground break-all">{v}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      {activity.thread?.id && (
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('activity.detail.thread')}</h3>
          <p className="mt-1 text-sm text-foreground">{entityLabel(activity.thread)}</p>
        </div>
      )}
    </aside>
  );
});

export default ActivityDetailRail;
